import clsx from 'clsx'
import { unsafeCSS } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { html } from 'lit/static-html.js'

import { GenericFormElement } from '@/internal/mixin/genericFormElement'

import styles from './_styles/rating.scss?inline'

/**
 * `dap-ds-rating`
 * @summary Rating is a component that allows users to rate a product or service.
 * @element dap-ds-rating
 * @title - Rating
 *
 * @event {{ value: number }} dds-change - Event fired when the rating value changes.
 *
 * @csspart base - The main rating container.
 * @csspart star - The star element.
 * @csspart stars-container - The container of the stars.
 * @csspart icon - The icon of the star.
 */
@customElement('dap-ds-rating')
export default class DapDSRating extends GenericFormElement {
  /** Reference to the live region for screen reader announcements */
  @query('.sr-announcement') private readonly _liveRegion: HTMLElement | null

  constructor() {
    super()

    this.value = 0
  }

  static readonly styles = unsafeCSS(styles)

  /** The label of the rating */
  @property({ reflect: true }) label: string

  /** The maximum number of stars */
  @property({ type: Number }) max = 5

  private _ratingContainer: HTMLElement | null = null
  @state() private _hasFocus = false
  @state() private _hoverValue = 0
  @state() private readonly _uniqueId =
    `star-rating-${window.crypto.randomUUID()}`

  connectedCallback() {
    super.connectedCallback()

    // Add event listener at document level to ensure we catch all keyboard events
    document.addEventListener('keydown', this._handleKeyDown)
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    document.removeEventListener('keydown', this._handleKeyDown)

    if (this._ratingContainer) {
      this._ratingContainer.removeEventListener('focus', () => {})
    }
  }

  firstUpdated() {
    // Set a reference to the container element for easier access
    this._ratingContainer = this.renderRoot.querySelector('.rating-container')
  }

  _handleContainerFocus = () => {
    this._hasFocus = true

    // Announce current rating for initial focus
    this._announceRating()
  }

  _handleContainerBlur = () => {
    this._hasFocus = false
  }

  _handleContainerKeyDown = (event: KeyboardEvent) => {
    if (this.disabled || this.readonly) return

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        event.preventDefault()
        this._incrementRating()
        break
      case 'ArrowLeft':
      case 'ArrowDown':
        event.preventDefault()
        this._decrementRating()
        break
      case 'Home':
        event.preventDefault()
        this._setRating(1)
        break
      case 'End':
        event.preventDefault()
        this._setRating(this.max)
        break
      default:
        return
    }
  }

  /**
   * Handle keyboard events for the entire component
   * This is a backup to catch keyboard events if they bubble up
   */
  _handleKeyDown = (event: KeyboardEvent) => {
    if (this.disabled || this.readonly) return

    // Only process keyboard events if:
    // 1. The event target is this component or its children
    // 2. The component currently has focus
    const isTargetWithin = this.contains(event.target as Node)

    if (isTargetWithin && this._hasFocus) {
      // The container's direct keydown handler should handle this,
      // but this is a backup in case the event bubbles up
      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowUp':
        case 'ArrowLeft':
        case 'ArrowDown':
        case 'Home':
        case 'End':
          // Let the container's direct keydown handler process these
          break
        default:
          return
      }
    }
  }

  _incrementRating() {
    const newValue = Math.min(this.value + 1, this.max)
    this._setRating(newValue)
  }

  _decrementRating() {
    const newValue = Math.max(this.value - 1, 0)
    this._setRating(newValue)
  }

  _setRating(value: number) {
    const oldValue = this.value
    this.value = value

    // Only announce if the value actually changed
    if (oldValue !== value) {
      this._announceRating()
      this.emit('dds-change', { value })
    }
  }

  /**
   * Announce the current rating to screen readers
   */
  _announceRating() {
    if (this._liveRegion) {
      const message =
        this.value === 0
          ? `Rating cleared`
          : `${this.value} of ${this.max} stars selected`

      // Update the live region with the current rating
      this._liveRegion.textContent = message
    }
  }

  _handleStarClick(value: number) {
    if (this.disabled || this.readonly) return

    // Toggle the value if the same star is clicked twice
    const newValue = this.value === value ? 0 : value
    this._setRating(newValue)
  }

  _handleStarHover(value: number) {
    if (this.disabled || this.readonly) return
    this._hoverValue = value
  }

  _handleStarLeave() {
    if (this.disabled || this.readonly) return
    this._hoverValue = 0
  }

  render() {
    const stars = []

    for (let i = 1; i <= this.max; i++) {
      const isActive = i <= this.value
      const isHovered =
        !this.disabled && !this.readonly && i <= this._hoverValue

      stars.push(html`
        <span
          part="star"
          class=${clsx('star', {
            'star--active': isActive,
            'star--hover': isHovered,
            'star--readonly': this.readonly,
            'star--disabled': this.disabled,
          })}
          @click=${() => this._handleStarClick(i)}
          @mouseenter=${() => this._handleStarHover(i)}
          @mouseleave=${() => this._handleStarLeave()}
          tabindex=${this.disabled ? '-1' : '0'}
          role="radio"
          aria-checked=${isActive}
          aria-disabled=${this.disabled}
          aria-posinset=${i}
          aria-setsize=${this.max}>
          <dap-ds-icon part="icon" name="star-fill"></dap-ds-icon>
          <span class="sr-only">${i} star${i === 1 ? '' : 's'}</span>
        </span>
      `)
    }

    return html`
      <div
        class="rating-container"
        part="base"
        tabindex=${this.disabled ? '-1' : '0'}
        @focus=${this._handleContainerFocus}
        @blur=${this._handleContainerBlur}
        @keydown=${this._handleContainerKeyDown}
        aria-labelledby="rating-label-${this._uniqueId}">
        ${(this, this.renderLabel(`rating-label-${this._uniqueId}`))}
        <div
          class="stars-container"
          part="stars-container"
          role="radiogroup"
          aria-labelledby="rating-label-${this._uniqueId}"
          aria-disabled=${this.disabled}
          aria-readonly=${this.readonly}>
          ${stars}
        </div>
        <!-- Live region for screen reader announcements -->
        <div class="sr-announcement sr-only" aria-live="assertive"></div>
      </div>
    `
  }
}
